/**
 * The Analytics page's SEEDED default views — the replacement for the old
 * Overview / Marketing / Legal / Logistics tab row.
 *
 * ## Why roles stopped being tabs
 *
 * A tab row asserts that the four groupings are exhaustive, mutually exclusive and
 * fixed by us. None of that is true: a brand's analyst wants marketing scans beside
 * legal transfer counts, and no fifth tab we could ship would be the one they wanted.
 * A saved view is the same idea without the assertion — the roles become *starting
 * points*, and the arrangement within a view becomes the user's.
 *
 * ## The risk this file carries
 *
 * Seeded defaults are LOAD-BEARING. A new user who has never saved a view lands on
 * `DEFAULT_ANALYTICS_VIEW_ID` and that is the entire product to them, so a seed that
 * references a card id which no longer exists degrades silently to a shorter page.
 * Two mitigations, both deliberate:
 *   1. Ids are declared once here (`ANALYTICS_TILE_IDS` / `ANALYTICS_PANEL_IDS`) and
 *      the seeds are built FROM those constants, so a rename is a compile error
 *      rather than a missing card.
 *   2. The canvas filters unknown ids rather than rendering a hole, and `Reset to
 *      default` always returns to the default seed — the escape hatch for the other
 *      risk, that two people at one brand now see different analytics.
 *
 * ## Why only ONE seed authors geometry
 *
 * `cards` is an ORDER, and `analyticsGrid`'s packer turns an order into a layout —
 * the same packer that places a card the user adds later. Leaving `layouts` off a
 * seed therefore means "packed", and it means packed by the code that will also be
 * packing everything added after it. Writing the coordinates out by hand would give
 * the seeds a second, forkable definition of the default arrangement, and the day it
 * drifted from the packer a seeded view and a freshly-added card would disagree about
 * what "the top row" is.
 *
 * Logistics is the exception, and it earns it: its two panels sit SIDE BY SIDE, which
 * is a real arrangement no ordering can express. It is also the demonstration that
 * layout is a per-view property rather than a global preference — the thing the old
 * stacked / side-by-side toggle was standing in for.
 *
 * Seeds are READ-ONLY. They are not written to user preferences, cannot be deleted,
 * and are recomputed from this file on every load — so improving a default improves
 * it for everyone who has not overridden it, which is the point of seeding rather
 * than provisioning rows.
 */
import type { SavedTableView, AnalyticsViewState } from 'types'

import { analyticsVariantCardId } from './analyticsGrid'
import { DEFAULT_CARD_IDS } from './roiCardDefinitions'

/**
 * Canonical tile ids.
 *
 * The four `*_count` ids are the keys of `GetAnalyticsTotalsCounts` — the same keys
 * `Overview` renders — so a tile id IS the totals field it displays. The ROI tile ids
 * come from `roiCardDefinitions` and are referenced through `DEFAULT_CARD_IDS` below
 * rather than restated, so the two lists cannot disagree.
 */
export const ANALYTICS_TILE_IDS = {
  scans: 'scans_count',
  certificates: 'certificates_count',
  completedTransfers: 'completed_transfers_count',
  inProgressTransfers: 'in_progress_transfers_count',
} as const

/**
 * Canonical panel ids — a panel is a card you STUDY, not one you scan.
 *
 * ⚠️ VRG-481: these are DERIVED now, not authored. A panel used to be its own card with a
 * Scans/Certificates toggle in its header (`scans_by_time`); it is now a `(metric,
 * display)` pair, so the chart panel IS `Total Scans` drawn as a chart. Building the id
 * with the shared helper rather than writing the string is what keeps this file and the
 * card the canvas actually renders from ever disagreeing about the separator.
 *
 * The two ids they REPLACE live in `analyticsDisplay`'s `LEGACY_ANALYTICS_PANEL_IDS`,
 * which is read only by the read-time forward map. Nothing new is ever written with them.
 */
export const ANALYTICS_PANEL_IDS = {
  scansByTime: analyticsVariantCardId(ANALYTICS_TILE_IDS.scans, 'chart'),
  scansByLocation: analyticsVariantCardId(ANALYTICS_TILE_IDS.scans, 'map'),
  certificatesByTime: analyticsVariantCardId(ANALYTICS_TILE_IDS.certificates, 'chart'),
} as const

const ALL_TILES = Object.values(ANALYTICS_TILE_IDS)

/** The two panels every general-purpose seed opens with: scans over time, scans on a map. */
const SCAN_PANELS = [ANALYTICS_PANEL_IDS.scansByTime, ANALYTICS_PANEL_IDS.scansByLocation]

/** Prefix that marks a view id as seeded (read-only) rather than user-saved. */
export const SEED_VIEW_PREFIX = 'seed:'

export const isSeededViewId = (id: string | null | undefined): boolean =>
  typeof id === 'string' && id.startsWith(SEED_VIEW_PREFIX)

const seed = (key: string, name: string, state: AnalyticsViewState): SavedTableView => ({
  id: `${SEED_VIEW_PREFIX}${key}`,
  name,
  // Seeded views live in the SAME `TableViewState` envelope as user-saved ones, so
  // `ViewsDropdown`'s comparison, application and persistence paths are identical for
  // both and there is no second code path to keep in step.
  state: { analytics: state },
  // Fixed, not `new Date()`: this object is recreated on every render, and a moving
  // `createdAt` would make `stableStringify` disagree with itself between renders and
  // flicker the active-view highlight off.
  createdAt: '1970-01-01T00:00:00.000Z',
})

/**
 * The default view. Everything on the page, packed, nothing filtered — the state a
 * first-time user should land in, and the state `Reset to default` returns to.
 */
export const DEFAULT_ANALYTICS_VIEW_ID = `${SEED_VIEW_PREFIX}overview`

export const DEFAULT_ANALYTICS_VIEW_STATE: AnalyticsViewState = {
  cards: [...ALL_TILES, ...SCAN_PANELS],
  filters: {},
}

export const ANALYTICS_SEEDED_VIEWS: SavedTableView[] = [
  seed('overview', 'Overview', DEFAULT_ANALYTICS_VIEW_STATE),

  // Marketing leads with the ROI cards that carry REAL values today — the same
  // reasoning as `DEFAULT_CARD_IDS.marketing`, where leading with a placeholder made
  // the tab read as broken on first load. Scan volume rides along because every
  // marketing question starts with "how many".
  // `filters` is EMPTY, and used to read `{ dataType: 'scans' }`. That key only ever
  // restated the default, and `currentState` now omits a default data type so that a
  // seed and the state applied from it can compare equal — so declaring it here would
  // make Marketing the one seed that de-selects itself the moment it is applied.
  seed('marketing', 'Marketing', {
    cards: [ANALYTICS_TILE_IDS.scans, ...DEFAULT_CARD_IDS.marketing, ...SCAN_PANELS],
    filters: {},
  }),

  // Legal is about the certificate and its chain of custody, not about scan volume —
  // so the tiles are certificate + transfer counts, and the map is dropped: where a
  // scan happened is a marketing/logistics question, and one panel gets the full width
  // the time axis wants.
  seed('legal', 'Legal', {
    cards: [
      ANALYTICS_TILE_IDS.certificates,
      ANALYTICS_TILE_IDS.completedTransfers,
      ...DEFAULT_CARD_IDS.legal,
      ANALYTICS_PANEL_IDS.certificatesByTime,
    ],
    /**
     * `filters` is EMPTY, and it used to read `{ chartDataType: 'certificates' }`.
     *
     * VRG-481 moved the dimension OUT of the filters and INTO the card's identity: this
     * seed now names `certificates_count::chart` directly, which is a chart of
     * certificates by construction rather than a chart of whatever the page's dimension
     * filter happened to say. That is strictly better for a seed — the card cannot be
     * repointed by a control elsewhere on the page — and it is what lets this seed compare
     * equal to the state applied from it (`currentState` omits a default dimension, so a
     * seed declaring one de-selected itself the moment it was applied).
     */
    filters: {},
  }),

  /**
   * Logistics is the one seed that authors geometry: its two questions are "when did
   * items move" and "where are they", and they are read AGAINST each other rather than
   * in sequence, so the panels go side by side at 6 columns each.
   *
   * FOUR tiles, not five, and that is a layout constraint rather than an editorial one.
   * The grid compacts vertically — cards float up into free space, which is what stops
   * a removed card leaving a hole. A fifth tile puts one 3-column card alone on row 1
   * and leaves columns 3 to 11 empty beside it, so the MAP (x = 6) floats up into that
   * row while the chart (x = 0) is blocked by the tile: measured, the two panels ended
   * up 184px out of vertical alignment, which is exactly what side-by-side is for and
   * exactly what it then failed to deliver. Four tiles fill row 0 completely and there
   * is nothing left for a panel to float into.
   *
   * The third logistics ROI card is not lost, it is one click away in the card
   * library — and a user who adds it gets it appended BELOW the panels, where it has
   * no row to break.
   *
   * ⚠️ These `x` values are on the 3-column-tile grid and are NOT independent of
   * `ANALYTICS_CARD_SIZE_DEFAULTS.tile.w`. `resolveLayout` re-applies `maxW` from the
   * current spec, so narrowing the tile without moving these would clamp each card's
   * width while leaving its authored `x` behind — a row with a hole after every card.
   * This is the only seed that authors geometry; see that constant for the measurements
   * behind the tile staying at 3.
   */
  seed('logistics', 'Logistics', {
    cards: [
      ANALYTICS_TILE_IDS.inProgressTransfers,
      ANALYTICS_TILE_IDS.completedTransfers,
      ...DEFAULT_CARD_IDS.logistics.slice(0, 2),
      ...SCAN_PANELS,
    ],
    layouts: {
      lg: [
        { i: ANALYTICS_TILE_IDS.inProgressTransfers, x: 0, y: 0, w: 3, h: 1 },
        { i: ANALYTICS_TILE_IDS.completedTransfers, x: 3, y: 0, w: 3, h: 1 },
        { i: DEFAULT_CARD_IDS.logistics[0], x: 6, y: 0, w: 3, h: 1 },
        { i: DEFAULT_CARD_IDS.logistics[1], x: 9, y: 0, w: 3, h: 1 },
        { i: ANALYTICS_PANEL_IDS.scansByTime, x: 0, y: 1, w: 6, h: 4 },
        { i: ANALYTICS_PANEL_IDS.scansByLocation, x: 6, y: 1, w: 6, h: 4 },
      ],
    },
    filters: {},
  }),
]

export const findSeededView = (id: string | null | undefined): SavedTableView | undefined =>
  ANALYTICS_SEEDED_VIEWS.find(v => v.id === id)
